import { SOURCE_LANGUAGE } from '../i18n/language-registry';
import type { LegalTexts } from './legal-document';

type Loader = () => Promise<{ readonly default: LegalTexts }>;

/**
 * Each language's texts as a chunk of their own, fetched the first time they are read — or
 * earlier, while the phone is idle, so they open without a network once the app has started.
 */
const LOADERS: Readonly<Record<string, Loader>> = {
  fr: () => import('./texts/fr'),
  en: () => import('./texts/en'),
  nl: () => import('./texts/nl'),
  de: () => import('./texts/de'),
  es: () => import('./texts/es'),
  it: () => import('./texts/it'),
  pt: () => import('./texts/pt'),
  sv: () => import('./texts/sv'),
  tr: () => import('./texts/tr'),
  ar: () => import('./texts/ar'),
  ur: () => import('./texts/ur'),
};

export const LEGAL_LANGUAGES: readonly string[] = Object.keys(LOADERS);

const loaded = new Map<string, Promise<LegalTexts>>();

/** The texts in a language; an unknown one reads the French, which binds anyway. */
export const loadLegalTexts = (language: string): Promise<LegalTexts> => {
  const code = language in LOADERS ? language : SOURCE_LANGUAGE;
  let texts = loaded.get(code);
  if (texts === undefined) {
    const loader = LOADERS[code] as Loader;
    texts = loader().then((module) => module.default);
    // A failed fetch is not remembered: the next look tries again.
    texts.catch(() => loaded.delete(code));
    loaded.set(code, texts);
  }
  return texts;
};
